import React, { useState, useEffect } from 'react';
import { AlertTriangle, RefreshCw, CheckCircle, Clock, XCircle, Eye, Edit2, ChevronDown, ChevronUp, Link } from 'lucide-react';
import api from '../../api';
import toast from 'react-hot-toast';
import ProcessMapDiff from './ProcessMapDiff';
import SOPPreviewModal from './SOPPreviewModal';
import IncrementalUpdateModal from './IncrementalUpdateModal';

interface MappingDetail {
  id: number;
  sop_section_number: string;
  sop_section_title: string;
  sync_status: string;
  modified_at: string;
  modified_by_id: number | null;
  modified_by_name: string;
  source_type: 'manual' | 'ai_generated' | 'process_map_sync' | 'sop_sync';
  validation_status: 'unvalidated' | 'validated' | 'needs_review';
  last_synced_at: string | null;
  created_at: string;
  process_step: {
    step_number: string;
    step_title: string;
  } | null;
  sop: {
    id: number;
    title: string;
  } | null;
}

interface SyncStatus {
  total_process_maps: number;
  total_steps: number;
  linked_steps: number;
  unlinked_steps: number;
  linkage_percentage: string;
  total_sops: number;
  out_of_sync_count: number;
  is_synced: boolean;
  orphaned_sections?: number; // 🔧 Fix Bug #12: Track orphaned SOP sections
  recent_mappings?: MappingDetail[]; // 🆕 New field
}

interface OutOfSyncWarningProps {
  projectId: number;
  showIfSynced?: boolean; // Show component even when synced (for status display)
  onRegenerateClick?: () => void;
  onProcessMapUpdate?: () => void; // NEW: Callback to refresh process map data
  onRegenerateSuccess?: () => Promise<void>; // 🔧 Fix Bug #11: Callback to refresh analysis results after regeneration
}

const OutOfSyncWarning: React.FC<OutOfSyncWarningProps> = ({
  projectId,
  showIfSynced = false,
  onRegenerateClick,
  onProcessMapUpdate,
  onRegenerateSuccess
}) => {
  const [syncStatus, setSyncStatus] = useState<SyncStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [showMappingDetails, setShowMappingDetails] = useState(false); // 🆕 For collapsible section
  const [showDiff, setShowDiff] = useState(false); // 🆕 For diff viewer
  const [processMapId, setProcessMapId] = useState<number | null>(null); // 🆕 Store process map ID
  const [showSOPPreview, setShowSOPPreview] = useState(false); // 🆕 For SOP Preview Modal
  const [showIncrementalUpdate, setShowIncrementalUpdate] = useState(false); // 🆕 For Incremental Update Modal
  const [isProcessMapCoverageCollapsed, setIsProcessMapCoverageCollapsed] = useState(false); // Collapse state for Process Map Coverage
  const [isSyncContentCollapsed, setIsSyncContentCollapsed] = useState(false); // Collapse state for entire sync content box
  const [isCreatingLinkage, setIsCreatingLinkage] = useState(false); // State for manual linkage creation

  // Helper function to get badge color for source type
  const getSourceTypeBadge = (sourceType: string) => {
    const badges = {
      'ai_generated': 'bg-purple-100 text-purple-700 border-purple-200',
      'manual': 'bg-blue-100 text-blue-700 border-blue-200',
      'process_map_sync': 'bg-green-100 text-green-700 border-green-200',
      'sop_sync': 'bg-yellow-100 text-yellow-700 border-yellow-200'
    };
    return badges[sourceType as keyof typeof badges] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Helper function to get badge color for validation status
  const getValidationBadge = (status: string) => {
    const badges = {
      'validated': 'bg-green-100 text-green-700 border-green-200',
      'unvalidated': 'bg-gray-100 text-gray-600 border-gray-200',
      'needs_review': 'bg-orange-100 text-orange-700 border-orange-200'
    };
    return badges[status as keyof typeof badges] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Helper function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get(`/api/analysis/sop-sync-status/${projectId}`);
      setSyncStatus(response.data.syncStatus);
      console.log('📊 [FETCH SYNC STATUS] Response:', response.data);

      // 🆕 Fetch process map ID for diff viewer
      try {
        // FIXED: Use correct API endpoint
        const processMapResponse = await api.get(`/api/process-maps/project/${projectId}?level=3`);

        console.log('🔍 [OutOfSyncWarning] Process map response:', processMapResponse.data);

        // Response structure: { process_maps: [...], total: N }
        if (processMapResponse.data?.process_maps && processMapResponse.data.process_maps.length > 0) {
          // Find Level 3 process map (preferred for incremental updates)
          const level3Map = processMapResponse.data.process_maps.find((map: any) => map.level === 3);
          const selectedMap = level3Map || processMapResponse.data.process_maps[0];

          console.log('✅ [OutOfSyncWarning] Found process map:', {
            id: selectedMap.id,
            level: selectedMap.level,
            title: selectedMap.title
          });

          setProcessMapId(selectedMap.id);
        } else {
          console.warn('⚠️ [OutOfSyncWarning] No process maps found for project', projectId);
          setProcessMapId(null);
        }
      } catch (pmError) {
        console.error('❌ [OutOfSyncWarning] Could not fetch process map ID:', pmError);
        setProcessMapId(null);
      }
    } catch (error) {
      console.error('[OutOfSyncWarning] Failed to fetch sync status:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSyncStatus();

    // Refresh sync status every 30 seconds
    const interval = setInterval(fetchSyncStatus, 30000);
    return () => clearInterval(interval);
  }, [projectId]);

  const handleRegenerate = async () => {
    if (onRegenerateClick) {
      onRegenerateClick();
      return;
    }

    setRegenerating(true);
    setProgress(0);
    setStatusMessage('Initiating SOP regeneration...');

    try {
      await api.post(`/api/analysis/regenerate-sop/${projectId}`, {});

      setProgress(10);
      setStatusMessage('SOP regeneration started. AI is processing...');
      toast.success('SOP regeneration initiated. This will take 1-2 minutes.');

      // Poll sync status every 10 seconds until synced or 5 minutes timeout
      let attempts = 0;
      const maxAttempts = 30; // 5 minutes (30 * 10 seconds)

      const pollInterval = setInterval(async () => {
        attempts++;

        // Update progress bar (10% to 90% over 3 minutes)
        const progressPercent = Math.min(10 + (attempts * 3), 90);
        setProgress(progressPercent);

        // Update status message
        if (attempts < 6) {
          setStatusMessage('Generating SOP content with AI...');
        } else if (attempts < 12) {
          setStatusMessage('Processing terminology and formatting...');
        } else if (attempts < 18) {
          setStatusMessage('Creating step-to-SOP linkages...');
        } else {
          setStatusMessage('Finalizing regeneration...');
        }

        try {
          const response = await api.get(`/api/analysis/sop-sync-status/${projectId}`);
          const status = response.data.syncStatus;

          if (status.is_synced) {
            // Synced! Stop polling
            clearInterval(pollInterval);
            setProgress(100);
            setStatusMessage('SOP regenerated successfully!');

            setTimeout(async () => {
              setRegenerating(false);
              setProgress(0);
              setStatusMessage('');
              toast.success('✅ SOP regenerated successfully! All steps are now in sync.');

              // 🔧 Fix Bug #11: Refresh analysis results to update SOP tabs
              if (onRegenerateSuccess) {
                await onRegenerateSuccess();
              }

              fetchSyncStatus();
            }, 1000);
          } else if (attempts >= maxAttempts) {
            // Timeout
            clearInterval(pollInterval);
            setRegenerating(false);
            setProgress(0);
            setStatusMessage('');
            toast.error('Regeneration is taking longer than expected. Please check back in a few minutes or refresh the page.');
          }
        } catch (err) {
          console.error('Polling error:', err);
        }
      }, 10000); // Poll every 10 seconds

    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to regenerate SOP');
      setRegenerating(false);
      setProgress(0);
      setStatusMessage('');
    }
  };

  const handleCreateLinkage = async () => {
    setIsCreatingLinkage(true);
    toast.loading('Creating linkages...');

    try {
      const response = await api.post(`/api/analysis/create-sop-linkage/${projectId}`);

      toast.dismiss();
      toast.success(`✅ Linkage created! ${response.data.mappings_created || 0} mappings`);

      console.log('🔄 [CREATE LINKAGE] Linkage created, refreshing sync status...');

      // Force immediate refresh of sync status FIRST
      await fetchSyncStatus();

      // Then refresh parent component (won't override our local state since we already fetched)
      if (onRegenerateSuccess) {
        console.log('🔄 [CREATE LINKAGE] Calling parent refresh callback...');
        await onRegenerateSuccess();
      }

      // Double-check: Fetch again after parent refresh to ensure we have latest data
      console.log('🔄 [CREATE LINKAGE] Final sync status check...');
      await fetchSyncStatus();
    } catch (error: any) {
      toast.dismiss();
      toast.error(error.response?.data?.error || 'Failed to create linkages');
    } finally {
      setIsCreatingLinkage(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center space-x-2 text-gray-500 text-sm">
        <Clock className="w-4 h-4 animate-spin" />
        <span>Checking sync status...</span>
      </div>
    );
  }

  if (!syncStatus) {
    return null;
  }

  // If synced and we shouldn't show when synced, return null
  if (syncStatus.is_synced && !showIfSynced) {
    return null;
  }

  // Enhanced state analysis for dynamic styling (from user's original implementation)
  const hasOutOfSync = syncStatus.out_of_sync_count > 0;
  const hasUnlinked = syncStatus.unlinked_steps > 0;
  const hasBoth = hasOutOfSync && hasUnlinked;

  // Dynamic color system: Red for unlinked (more critical), Amber for out-sync only
  const isRedState = hasUnlinked;
  const bgColor = isRedState ? 'bg-red-50' : 'bg-amber-50';
  const borderColor = isRedState ? 'border-red-300' : 'border-amber-300';
  const iconColor = isRedState ? 'text-red-600' : 'text-amber-600';
  const headingColor = isRedState ? 'text-red-900' : 'text-amber-900';
  const textColor = isRedState ? 'text-red-700' : 'text-amber-700';
  const buttonBg = isRedState ? 'bg-red-600 hover:bg-red-700 disabled:bg-red-400' : 'bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400';
  const cardBg = isRedState ? 'border-red-200' : 'border-amber-200';
  const cardBorderColor = isRedState ? 'bg-red-100 border-red-300' : 'bg-amber-100 border-amber-300';
  const statusTextColor = isRedState ? 'text-red-800' : 'text-amber-800';
  const sectionBorderColor = isRedState ? 'border-red-200' : 'border-amber-200';
  const sectionTextColor = isRedState ? 'text-red-900 hover:text-red-700' : 'text-amber-900 hover:text-amber-700';

  // Synced state (preserving other developer's action buttons)
  const syncedContent = syncStatus.is_synced && (
    <div className="bg-green-50 border border-green-200 rounded-lg p-4">
      <div className="flex items-start space-x-3">
        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-green-900 mb-1">
            Process Maps and SOPs are in sync
          </h4>
          <p className="text-sm text-green-700 mb-3">
            {syncStatus.total_steps} process steps linked to {syncStatus.total_sops} SOP(s)
            ({syncStatus.linkage_percentage}% coverage)
          </p>

          {/* 🔧 Fix Bug #12: Show orphaned sections info */}
          {(syncStatus.orphaned_sections ?? 0) > 0 && (
            <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-sm">
              <p className="text-blue-800">
                ℹ️ <strong>Note:</strong> {syncStatus.orphaned_sections ?? 0} SOP section{(syncStatus.orphaned_sections ?? 0) > 1 ? 's' : ''} linked to deleted process step{(syncStatus.orphaned_sections ?? 0) > 1 ? 's' : ''}.
                Regenerate SOP to clean up orphaned sections.
              </p>
            </div>
          )}

          {/* Action Buttons (available even when synced) */}
          {processMapId && (
            <div className="flex items-center space-x-3 mb-3">
              {/* Regenerate SOP Button */}
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
              >
                {regenerating ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    <span>Regenerating SOP...</span>
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    <span>Regenerate SOP</span>
                  </>
                )}
              </button>

              {/* SOP Preview Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('👁️ Preview SOP clicked! Current state:', { showSOPPreview, processMapId });
                  setShowSOPPreview(true);
                  console.log('👁️ Preview SOP state set to true');
                }}
                disabled={regenerating}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
              >
                <Eye className="w-4 h-4" />
                <span>Preview SOP</span>
              </button>

              {/* Quick Edit Button */}
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  console.log('✏️ Quick Edit clicked! Current state:', { showIncrementalUpdate, processMapId });
                  setShowIncrementalUpdate(true);
                  console.log('✏️ Quick Edit state set to true');
                }}
                disabled={regenerating}
                className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                <span>Quick Edit</span>
              </button>
            </div>
          )}

          {/* Simple status message when regenerating */}
          {regenerating && (
            <div className="mb-3 bg-green-100 rounded-lg p-3 border border-green-300">
              <p className="text-sm text-green-800">
                ⏱️ SOP regeneration in progress. This typically takes 1-2 minutes. Please don't refresh the page.
              </p>
            </div>
          )}

          {/* 🆕 View Diff Button (even when synced - for testing/verification) */}
          {processMapId && (
            <div className="mt-3 pt-3 border-t border-green-200">
              <button
                onClick={() => setShowDiff(!showDiff)}
                className="flex items-center space-x-2 text-sm font-medium text-green-900 hover:text-green-700 transition-colors"
              >
                <span>{showDiff ? '▼' : '▶'}</span>
                <span>View Process Map Details</span>
              </button>

              {showDiff && (
                <div className="mt-3">
                  <ProcessMapDiff processMapId={processMapId} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  // Return appropriate content
  return (
    <>
      {/* Show synced content OR out-of-sync content with dynamic styling */}
      {syncedContent || (
        <div className={`${bgColor} border ${borderColor} rounded-lg p-4`}>
          <div className={`flex ${isSyncContentCollapsed ? 'items-center' : 'items-start'} space-x-3`}>
            {/* Expand/Collapse button */}
            <button
              onClick={() => setIsSyncContentCollapsed(!isSyncContentCollapsed)}
              className={`p-1 hover:bg-black/10 rounded transition-colors flex-shrink-0 ${isSyncContentCollapsed ? '' : 'mt-0.5'}`}
              title={isSyncContentCollapsed ? 'Expand' : 'Collapse'}
            >
              {isSyncContentCollapsed ? (
                <ChevronDown className={`w-4 h-4 ${iconColor}`} />
              ) : (
                <ChevronUp className={`w-4 h-4 ${iconColor}`} />
              )}
            </button>
            <AlertTriangle className={`w-5 h-5 ${iconColor} ${isSyncContentCollapsed ? '' : 'mt-0.5'} flex-shrink-0`} />
            <div className="flex-1">
              <h4 className={`text-sm font-semibold ${headingColor} ${isSyncContentCollapsed ? '' : 'mb-1'}`}>
                {hasBoth
                  ? 'Process Maps and SOPs are out of sync'
                  : hasOutOfSync
                    ? 'Process Maps have been updated'
                    : 'Low Process Map Coverage'}
              </h4>
              {!isSyncContentCollapsed && (
              <>
              <p className={`text-sm ${textColor} mb-3`}>
                {hasBoth
                  ? `${syncStatus.out_of_sync_count} steps out of sync, ${syncStatus.unlinked_steps} steps not linked`
                  : hasOutOfSync
                    ? `${syncStatus.out_of_sync_count} step(s) are out of sync with the SOP. Regenerate the SOP to reflect the latest process changes.`
                    : `${syncStatus.unlinked_steps} steps not linked to SOP (minimum 80% coverage required)`}
              </p>

              {/* Process Map Coverage Section with Collapse Button */}
              <div className="mb-3">
                <button
                  onClick={() => setIsProcessMapCoverageCollapsed(!isProcessMapCoverageCollapsed)}
                  className="flex items-center gap-2 text-sm font-medium mb-2 hover:opacity-80 transition-opacity"
                >
                  {isProcessMapCoverageCollapsed ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronUp className="w-4 h-4" />
                  )}
                  <span>Process Map Coverage</span>
                </button>

                {!isProcessMapCoverageCollapsed && (
                  <div className="grid grid-cols-3 gap-3 text-xs">
                    <div className={`bg-white rounded p-2 border ${cardBg}`}>
                      <div className={`${iconColor} font-medium`}>Process Maps</div>
                      <div className={`text-lg font-bold ${headingColor}`}>{syncStatus.total_process_maps}</div>
                    </div>
                    <div className={`bg-white rounded p-2 border ${cardBg}`}>
                      <div className={`${iconColor} font-medium`}>Total Steps</div>
                      <div className={`text-lg font-bold ${headingColor}`}>{syncStatus.total_steps}</div>
                    </div>
                    <div className={`bg-white rounded p-2 border ${cardBg}`}>
                      <div className={`${iconColor} font-medium`}>Coverage</div>
                      <div className={`text-lg font-bold ${headingColor}`}>{syncStatus.linkage_percentage}%</div>
                    </div>
                  </div>
                )}
              </div>

              {/* Simple status message when regenerating */}
              {regenerating && (
                <div className={`mb-3 ${cardBorderColor} rounded-lg p-3 border`}>
                  <p className={`text-sm ${statusTextColor}`}>
                    ⏱️ SOP regeneration in progress. This typically takes 1-2 minutes. Please don't refresh the page.
                  </p>
                </div>
              )}

              <div className="flex items-center space-x-3">
                {/* Create Linkage Button */}
                <button
                  onClick={handleCreateLinkage}
                  disabled={isCreatingLinkage || regenerating}
                  className="bg-purple-600 hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
                  title="Link existing SOP to process map steps without regenerating"
                >
                  {isCreatingLinkage ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Linking...</span>
                    </>
                  ) : (
                    <>
                      <Link className="w-4 h-4" />
                      <span>Create Linkage</span>
                    </>
                  )}
                </button>

                {/* Main Regenerate Button */}
                <button
                  onClick={handleRegenerate}
                  disabled={regenerating || isCreatingLinkage}
                  className={`${buttonBg} disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors`}
                >
                  {regenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      <span>Regenerating SOP...</span>
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4" />
                      <span>Regenerate SOP</span>
                    </>
                  )}
                </button>

                {/* New Action Buttons (preserving other developer's improvements) */}
                {processMapId && (
                  <>
                    {/* SOP Preview Button */}
                    <button
                      onClick={() => setShowSOPPreview(true)}
                      disabled={regenerating}
                      className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                      <span>Preview SOP</span>
                    </button>

                    {/* Quick Edit Button */}
                    <button
                      onClick={() => setShowIncrementalUpdate(true)}
                      disabled={regenerating}
                      className="bg-green-600 hover:bg-green-700 disabled:bg-green-400 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center space-x-2 transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                      <span>Quick Edit</span>
                    </button>
                  </>
                )}
              </div>

              {/* 🆕 Recent Mapping Details Section (with dynamic styling) */}
              {syncStatus.recent_mappings && syncStatus.recent_mappings.length > 0 && (
                <div className={`mt-4 border-t ${sectionBorderColor} pt-4`}>
                  <button
                    onClick={() => setShowMappingDetails(!showMappingDetails)}
                    className={`flex items-center space-x-2 text-sm font-medium ${sectionTextColor} transition-colors`}
                  >
                    <span>{showMappingDetails ? '▼' : '▶'}</span>
                    <span>Recent Mapping Changes ({syncStatus.recent_mappings.length})</span>
                  </button>

                  {showMappingDetails && (
                    <div className="mt-3 space-y-2">
                      {syncStatus.recent_mappings.map((mapping) => (
                        <div
                          key={mapping.id}
                          className={`bg-white rounded-lg p-3 border ${cardBg} text-xs`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex-1">
                              <div className={`font-medium ${headingColor}`}>
                                {mapping.sop_section_number} - {mapping.sop_section_title}
                              </div>
                              {mapping.process_step && (
                                <div className="text-gray-600 mt-1">
                                  Step {mapping.process_step.step_number}: {mapping.process_step.step_title}
                                </div>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2 flex-wrap gap-2">
                            {/* Source Type Badge */}
                            <span className={`px-2 py-1 rounded border text-xs font-medium ${getSourceTypeBadge(mapping.source_type)}`}>
                              {mapping.source_type === 'ai_generated' && '🤖 AI Generated'}
                              {mapping.source_type === 'manual' && '✋ Manual'}
                              {mapping.source_type === 'process_map_sync' && '🔄 Process Map Sync'}
                              {mapping.source_type === 'sop_sync' && '📄 SOP Sync'}
                            </span>

                            {/* Validation Status Badge */}
                            <span className={`px-2 py-1 rounded border text-xs font-medium ${getValidationBadge(mapping.validation_status)}`}>
                              {mapping.validation_status === 'validated' && '✅ Validated'}
                              {mapping.validation_status === 'unvalidated' && '⏳ Unvalidated'}
                              {mapping.validation_status === 'needs_review' && '⚠️ Needs Review'}
                            </span>

                            {/* Sync Status Badge */}
                            {mapping.sync_status === 'out_of_sync' && (
                              <span className="px-2 py-1 rounded border bg-red-100 text-red-700 border-red-200 text-xs font-medium">
                                🔴 Out of Sync
                              </span>
                            )}
                            {mapping.sync_status === 'synced' && (
                              <span className="px-2 py-1 rounded border bg-green-100 text-green-700 border-green-200 text-xs font-medium">
                                ✅ Synced
                              </span>
                            )}
                          </div>

                          <div className="mt-2 text-gray-500 flex items-center justify-between">
                            <span>Last modified by {mapping.modified_by_name}</span>
                            <span>{formatDate(mapping.modified_at)}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 🆕 Detailed Diff Viewer Section (with dynamic styling) */}
              {processMapId && (
                <div className={`mt-4 border-t ${sectionBorderColor} pt-4`}>
                  <button
                    onClick={() => setShowDiff(!showDiff)}
                    className={`flex items-center space-x-2 text-sm font-medium ${sectionTextColor} transition-colors`}
                  >
                    <span>{showDiff ? '▼' : '▶'}</span>
                    <span>View Detailed Diff</span>
                  </button>

                  {showDiff && (
                    <div className="mt-3">
                      <ProcessMapDiff processMapId={processMapId} />
                    </div>
                  )}
                </div>
              )}
              </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal Components - Always rendered when processMapId exists */}
      {processMapId && (
        <>
          <SOPPreviewModal
            isOpen={showSOPPreview}
            onClose={() => {
              console.log('🔒 SOPPreviewModal closing');
              setShowSOPPreview(false);
            }}
            processMapId={processMapId}
            processMapLevel={3}
            projectName="Project"
          />

          <IncrementalUpdateModal
            isOpen={showIncrementalUpdate}
            onClose={() => {
              console.log('🔒 IncrementalUpdateModal closing');
              setShowIncrementalUpdate(false);
            }}
            processMapId={processMapId}
            projectId={projectId}
            onUpdateSuccess={() => {
              console.log('🎯 [OutOfSyncWarning] IncrementalUpdateModal onUpdateSuccess called');

              fetchSyncStatus(); // Refresh sync status after update
              onProcessMapUpdate?.(); // NEW: Refresh process map data in parent

              // NEW: Emit global event for process map update
              console.log('📡 [OutOfSyncWarning] Dispatching processMapUpdated event:', {
                processMapId,
                projectId,
                timestamp: new Date().toISOString()
              });

              window.dispatchEvent(new CustomEvent('processMapUpdated', {
                detail: { processMapId, projectId }
              }));

              console.log('✅ [OutOfSyncWarning] processMapUpdated event dispatched successfully');

              toast.success('Process map updated successfully!');
            }}
          />
        </>
      )}
    </>
  );
};

export default OutOfSyncWarning;
